import * as Dialog from "@radix-ui/react-dialog";
import { useState } from "react";
import { createStructure } from "../api/chains";
import { STRUCTURE_KINDS } from "../lib/structureKinds";
import type { StructureKind, System } from "../types/chain";

interface StructureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chainId: string;
  system: System;
  updateSystem: (id: string, patch: Partial<System>) => void;
}

export function StructureDialog({
  open,
  onOpenChange,
  chainId,
  system,
  updateSystem,
}: StructureDialogProps) {
  const [kind, setKind] = useState<StructureKind>("Astrahus");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createStructure(chainId, system.id, { kind, name });
      updateSystem(system.id, {
        structures: [...(system.structures ?? []), created],
      });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add structure.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="modal__overlay" />
        <Dialog.Content className="modal__content" aria-describedby={undefined}>
          <Dialog.Title className="modal__title">Add structure</Dialog.Title>
          <dl className="form">
            <div className="form__row">
              <dt className="form__label">Type</dt>
              <dd className="form__value">
                <select
                  className="form__input"
                  value={kind}
                  onChange={(e) => setKind(e.target.value as StructureKind)}
                >
                  {STRUCTURE_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </dd>
            </div>
            <div className="form__row">
              <dt className="form__label">Name</dt>
              <dd className="form__value">
                <input
                  className="form__input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void add();
                    }
                  }}
                />
              </dd>
            </div>
          </dl>
          {error && (
            <p className="form__hint form__hint--error" role="alert">
              {error}
            </p>
          )}
          <div className="modal__actions">
            <button
              type="button"
              className="modal__btn"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="modal__btn modal__btn--primary"
              onClick={add}
              disabled={busy}
            >
              {busy ? "Adding…" : "Add"}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
